import { useState } from "react";
import { Minus, Plus, UserPlus, ChevronRight, Check, Sparkles, Loader2, Square, BookmarkPlus, Trash2, Share2, Timer, Info } from "lucide-react";
import VarianceModal from "@/components/VarianceModal";
import SessionSummaryModal from "@/components/SessionSummaryModal";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { cn, formatCurrency } from "@/lib/utils";
import type { NewSessionViewProps } from "@/ui/view-props";

function StepDots({ step }: { step: 1 | 2 | 3 }) {
  return (
    <div className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-full border border-white/5">
      {([1, 2, 3] as const).map((s) => (
        <div
          key={s}
          className={cn(
            "h-1.5 rounded-full transition-all duration-300",
            s === step ? "w-6 bg-neon-cyan shadow-[0_0_8px_hsl(var(--neon-cyan))]" : 
            s < step ? "w-1.5 bg-neon-emerald" : "w-1.5 bg-neon-border"
          )}
        />
      ))}
    </div>
  );
}

export function NewSessionNeonView(p: NewSessionViewProps) {
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [deleteTemplateId, setDeleteTemplateId] = useState<string | null>(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  const stepLabel = p.step === 1 ? "Select Players" : p.step === 2 ? "Track Buy-ins" : "Cash Out";
  const totalPot = p.sessionPlayers.reduce((sum, sp) => sum + (sp.buyIns * p.buyInAmount), 0);

  return (
    <div className="animate-fade-in flex flex-col gap-6">

      {/* ── Header / stepper ──────────────────────────────── */}
      <div className="flex items-center justify-between px-1">
        <div>
          <h1 className="text-2xl font-black text-white tracking-tight uppercase leading-none">New Session</h1>
          <div className="text-xs text-neon-muted font-bold tracking-widest mt-1 flex items-center gap-1.5">
            <span className="w-1 h-1 rounded-full bg-neon-cyan" />
            {stepLabel.toUpperCase()}
          </div>
        </div>
        <StepDots step={p.step} />
      </div>

      {/* ── Step 1: Player selection ────────────────────────── */}
      {p.step === 1 && (
        <div className="animate-slide-up flex flex-col gap-6">
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="text-[10px] font-black text-neon-muted uppercase tracking-widest">
                Selected Players ({p.selectedIds.size})
              </div>
              {p.selectedIds.size > 0 && (
                <div className="text-[10px] font-black text-neon-cyan uppercase bg-neon-cyan/10 px-2 py-0.5 rounded border border-neon-cyan/20">
                  {p.selectedIds.size} PLAYERS
                </div>
              )}
            </div>
            
            <div className="flex flex-wrap gap-2.5">
              {p.players.map((pl) => {
                const selected = p.selectedIds.has(pl.id);
                return (
                  <button
                    key={pl.id}
                    type="button"
                    className={cn(
                      "quick-pill flex items-center gap-1.5 px-4 py-2 text-xs font-bold transition-all",
                      selected ? "bg-neon-cyan/20 border-neon-cyan/60 text-neon-cyan shadow-[0_0_15px_hsl(var(--neon-cyan)/0.1)]" : ""
                    )}
                    data-testid={`player-select-${pl.name}`}
                    onClick={() => p.togglePlayer(pl.id)}
                  >
                    {selected ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5 opacity-40" />}
                    {pl.name}
                  </button>
                );
              })}

              {!p.showAddPlayer && (
                <button
                  type="button"
                  className="quick-pill border-dashed opacity-60 flex items-center gap-1.5 px-4 py-2 text-xs font-bold"
                  onClick={() => p.setShowAddPlayer(true)}
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  Add New
                </button>
              )}
            </div>

            {p.showAddPlayer && (
              <div className="flex gap-2 mt-6 animate-slide-up">
                <input
                  className="neon-input flex-1 bg-white/5"
                  placeholder="Player name"
                  value={p.newPlayerName}
                  onChange={(e) => p.setNewPlayerName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && p.addPlayerInline()}
                  autoFocus
                />
                <button type="button" className="neon-btn-primary px-6" onClick={p.addPlayerInline}>
                  ADD
                </button>
                <button type="button" className="neon-btn-secondary px-3" onClick={() => { p.setShowAddPlayer(false); p.setNewPlayerName(""); }}>
                  ✕
                </button>
              </div>
            )}
          </div>

          {/* Session Templates */}
          {p.showTemplatesSection && (
            <div className="glass-card p-6">
              <div className="text-[10px] font-black text-neon-muted uppercase tracking-widest mb-4">
                Saved Templates
              </div>
              <div className="flex flex-wrap gap-2">
                {(p.templates ?? []).map(tmpl => (
                  <div key={tmpl.id} className="flex items-center bg-white/5 border border-white/10 rounded-full pr-1 overflow-hidden">
                    <button
                      type="button"
                      className="px-4 py-1.5 text-[11px] font-bold text-white/80 hover:text-white transition-colors"
                      onClick={() => p.onLoadTemplate?.(tmpl)}
                    >
                      {tmpl.name}
                    </button>
                    <button
                      type="button"
                      className="p-1 text-neon-muted hover:text-neon-crimson transition-colors"
                      onClick={() => setDeleteTemplateId(tmpl.id)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                
                {showSaveTemplate ? (
                  <div className="flex gap-2 w-full mt-2 animate-slide-up">
                    <input
                      className="neon-input flex-1 bg-white/5 text-sm"
                      placeholder="Template name"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      autoFocus
                    />
                    <button
                      type="button"
                      className="neon-btn-primary px-4 py-1 text-xs"
                      disabled={!templateName.trim() || savingTemplate}
                      onClick={async () => {
                        setSavingTemplate(true);
                        await p.onSaveTemplate?.(templateName.trim());
                        setTemplateName('');
                        setShowSaveTemplate(false);
                        setSavingTemplate(false);
                      }}
                    >
                      SAVE
                    </button>
                    <button
                      type="button"
                      className="neon-btn-secondary px-3 text-xs"
                      onClick={() => { setShowSaveTemplate(false); setTemplateName(''); }}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={cn(
                      "quick-pill border-dashed border-neon-cyan/40 text-neon-cyan/60 flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase",
                      p.selectedIds.size < 2 && "opacity-30 cursor-not-allowed"
                    )}
                    disabled={p.selectedIds.size < 2}
                    onClick={() => setShowSaveTemplate(true)}
                  >
                    <BookmarkPlus className="w-3 h-3" />
                    Save Current
                  </button>
                )}
              </div>
              <ConfirmDialog
                open={deleteTemplateId !== null}
                onOpenChange={(open) => { if (!open) setDeleteTemplateId(null); }}
                title="Delete Template"
                description="Are you sure you want to delete this template?"
                confirmLabel="Delete"
                onConfirm={() => { if (deleteTemplateId) p.onDeleteTemplate?.(deleteTemplateId); setDeleteTemplateId(null); }}
              />
            </div>
          )}

          {/* Buy-in unit */}
          <div className="glass-card p-6">
            <div className="text-[10px] font-black text-neon-muted uppercase tracking-widest mb-4">
              Default Buy-in (Points)
            </div>
            <div className="flex gap-3 flex-wrap items-center">
              {[10, 20, 50, 100].map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className={cn(
                    "stepper-btn w-12 h-12 text-sm font-bold",
                    p.buyInUnit === preset ? "bg-neon-cyan/20 border-neon-cyan text-neon-cyan shadow-[0_0_15px_hsl(var(--neon-cyan)/0.2)]" : "bg-transparent"
                  )}
                  onClick={() => p.setBuyInUnit(preset)}
                >
                  {preset}
                </button>
              ))}
              <div className="flex-1 min-w-[100px]">
                <input
                  type="number"
                  inputMode="numeric"
                  className="neon-input bg-white/5 text-center font-black"
                  value={p.buyInUnit}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (v > 0) p.setBuyInUnit(v);
                  }}
                />
              </div>
            </div>
          </div>

          <div className="sticky-mobile-footer">
            <button
              type="button"
              className="neon-btn-primary w-full py-4 flex items-center justify-center gap-2 rounded-2xl shadow-xl shadow-neon-purple/20"
              onClick={p.startSession}
              disabled={p.selectedIds.size < 2}
            >
              <span className="font-black uppercase tracking-widest">Start Session</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* ── Step 2: Buy-in tracker ──────────────────────────── */}
      {p.step === 2 && (
        <div className="animate-slide-up flex flex-col gap-6">
          <div className="flex items-center justify-between px-1">
            <div className="flex items-center gap-3">
              <div className="px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 flex items-center gap-2">
                <Timer className="w-4 h-4 text-neon-cyan" />
                <span className="text-xs font-bold text-white tabular-nums">{p.elapsedTime || "00:00:00"}</span>
              </div>
              <div className="px-3 py-1.5 rounded-xl bg-neon-cyan/10 border border-neon-cyan/20 flex items-center gap-2">
                <span className="text-[10px] font-black text-neon-cyan uppercase">Pot: {totalPot}</span>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {p.onShareLiveLink && (
                <button
                  type="button"
                  className="p-2.5 rounded-xl bg-white/5 border border-white/10 text-white/70 hover:text-white transition-colors"
                  onClick={p.onShareLiveLink}
                  title="Share Live"
                >
                  <Share2 className="w-4 h-4" />
                </button>
              )}
              <button
                type="button"
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all",
                  p.roastGenerating ? "bg-white/5 text-neon-muted" : "bg-neon-cyan/20 text-neon-cyan border border-neon-cyan/30"
                )}
                disabled={p.roastGenerating}
                onClick={p.onRoastSession}
              >
                {p.roastGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                ROAST
              </button>
            </div>
          </div>

          {p.roast && (
            <div className="glass-card p-4 border-neon-cyan/30 bg-neon-cyan/5 animate-slide-up">
              <div className="flex gap-3">
                <Sparkles className="w-5 h-5 text-neon-cyan shrink-0" />
                <p className="text-xs font-medium leading-relaxed italic text-neon-cyan/90">
                  {p.roast}
                </p>
              </div>
            </div>
          )}

          <ConfirmDialog
            open={!!p.confirmRemovePlayerId}
            onOpenChange={(open) => { if (!open) p.cancelRemovePlayer(); }}
            title="Remove player?"
            description={`Remove this player from the session? All their buy-ins will be lost.`}
            confirmLabel="Remove"
            onConfirm={p.confirmRemovePlayer}
          />

          <div className="flex flex-col gap-3">
            {p.sessionPlayers.map((sp) => (
              <div
                key={sp.playerId}
                className="glass-card p-4 flex items-center gap-4 hover:border-neon-cyan/40"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-black text-white truncate">{sp.name}</span>
                    <button
                      type="button"
                      onClick={() => p.removePlayerFromSession(sp.playerId)}
                      className="text-neon-muted hover:text-neon-crimson p-1 transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="text-[10px] font-bold text-neon-muted uppercase">
                    {sp.buyIns} × {p.buyInAmount} = <span className="text-neon-cyan">{sp.buyIns * p.buyInAmount}</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    className="stepper-btn w-10 h-10 border-white/10"
                    onClick={() => p.removeBuyIn(sp.playerId)}
                    disabled={sp.buyIns <= 1}
                  >
                    <Minus className="w-5 h-5" />
                  </button>
                  <span className="text-xl font-black text-white tabular-nums min-w-[1.5rem] text-center">
                    {sp.buyIns}
                  </span>
                  <button
                    type="button"
                    className="stepper-btn w-10 h-10 border-neon-cyan/50 bg-neon-cyan/10 text-neon-cyan shadow-[0_0_10px_hsl(var(--neon-cyan)/0.2)]"
                    onClick={() => p.addBuyIn(sp.playerId)}
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Add mid-session */}
          <div className="glass-card p-5">
            <div className="text-[10px] font-black text-neon-muted uppercase tracking-widest mb-4">
              Add Player Mid-Session
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              {p.availableMidSession.map((pl) => (
                <button
                  key={pl.id}
                  type="button"
                  className="quick-pill py-1.5 px-3 text-[10px]"
                  onClick={() => p.addPlayerMidSession(pl)}
                >
                  <Plus className="w-3 h-3" /> {pl.name}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                className="neon-input flex-1 bg-white/5 text-sm"
                placeholder="New player name..."
                value={p.midSessionNewPlayerName}
                onChange={(e) => p.setMidSessionNewPlayerName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') p.addNewPlayerMidSession(p.midSessionNewPlayerName); }}
              />
              <button
                type="button"
                className="neon-btn-primary px-4"
                disabled={!p.midSessionNewPlayerName.trim()}
                onClick={() => p.addNewPlayerMidSession(p.midSessionNewPlayerName)}
              >
                ADD
              </button>
            </div>
          </div>

          <div className="sticky-mobile-footer">
            <button
              type="button"
              className="neon-btn-primary w-full py-4 flex items-center justify-center gap-2 rounded-2xl shadow-xl shadow-neon-cyan/20"
              onClick={() => p.setStep(3)}
            >
              <span className="font-black uppercase tracking-widest">Finish & Cash Out</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* ── Step 3: Cash-out entry ──────────────────────────── */}
      {p.step === 3 && (
        <div className="animate-slide-up flex flex-col gap-6">
          <div className="glass-card p-4 border-neon-cyan/20 bg-neon-cyan/5">
            <div className="flex items-center gap-3">
              <Info className="w-5 h-5 text-neon-cyan" />
              <div>
                <div className="text-[10px] font-black text-neon-cyan uppercase tracking-wider">Total Pot to Clear</div>
                <div className="text-xl font-black text-white tabular-nums">{totalPot}</div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {p.sessionPlayers.map((sp) => (
              <div
                key={sp.playerId}
                className="glass-card p-5 flex flex-col gap-4"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-base font-black text-white">{sp.name}</div>
                    <div className="text-[10px] font-bold text-neon-muted uppercase">
                      Invested: <span className="text-neon-cyan">{sp.buyIns * p.buyInAmount}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-black text-neon-muted uppercase mb-1">Cashed Out</div>
                    <input
                      type="number"
                      className="neon-input w-32 bg-white/5 text-right font-black text-lg focus:border-neon-cyan focus:shadow-[0_0_15px_hsl(var(--neon-cyan)/0.2)]"
                      placeholder="0"
                      value={sp.cashOut}
                      onChange={(e) => p.updateCashOut(sp.playerId, e.target.value)}
                    />
                  </div>
                </div>
                
                {/* Quick cashout pills */}
                <div className="flex flex-wrap gap-2">
                  {[0, sp.buyIns * p.buyInAmount, (sp.buyIns * p.buyInAmount) + 10, (sp.buyIns * p.buyInAmount) + 20, (sp.buyIns * p.buyInAmount) + 50].filter(v => v >= 0).map((val) => (
                    <button
                      key={val}
                      type="button"
                      className={cn(
                        "quick-pill text-[10px] py-1 px-3 border-white/10",
                        Number(sp.cashOut) === val && "bg-neon-cyan/20 border-neon-cyan text-neon-cyan shadow-[0_0_10px_hsl(var(--neon-cyan)/0.2)]"
                      )}
                      onClick={() => p.updateCashOut(sp.playerId, val.toString())}
                    >
                      {val === 0 ? "Bust" : val === sp.buyIns * p.buyInAmount ? "Even" : `+${val - (sp.buyIns * p.buyInAmount)}`}
                    </button>
                  ))}
                  <button 
                    type="button" 
                    className="quick-pill text-[10px] py-1 px-3 border-white/10"
                    onClick={() => {
                      const current = Number(sp.cashOut) || 0;
                      p.updateCashOut(sp.playerId, (current + 5).toString());
                    }}
                  >
                    +5
                  </button>
                  <button 
                    type="button" 
                    className="quick-pill text-[10px] py-1 px-3 border-white/10"
                    onClick={() => {
                      const current = Number(sp.cashOut) || 0;
                      p.updateCashOut(sp.playerId, (current + 20).toString());
                    }}
                  >
                    +20
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="sticky-mobile-footer">
            <button
              type="button"
              className="neon-btn-primary w-full py-4 flex items-center justify-center gap-2 rounded-2xl shadow-xl shadow-neon-emerald/40 !bg-neon-emerald"
              disabled={p.saving || p.sessionPlayers.some((sp) => sp.cashOut === "")}
              onClick={p.attemptEndSession}
            >
              {p.saving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Check className="w-5 h-5" />
              )}
              <span className="font-black uppercase tracking-widest">
                {p.saving ? "Saving Session..." : "Finalize & Save"}
              </span>
            </button>
          </div>
        </div>
      )}

      {/* modals */}
      <VarianceModal
        open={p.showVarianceModal}
        onClose={() => p.setShowVarianceModal(false)}
        variance={p.varianceAmount}
        totalBuyIns={p.sessionPlayers.reduce((s, r) => s + r.buyIns * p.buyInAmount, 0)}
        totalCashOuts={p.sessionPlayers.reduce((s, r) => s + (parseFloat(r.cashOut) || 0), 0)}
        sessionPlayers={p.sessionPlayers}
        onSplitEqually={(adj) => p.handleVarianceResolved(adj, 'split')}
        onAssignToPlayer={(adj) => p.handleVarianceResolved(adj, 'manual')}
        onReenter={() => p.setShowVarianceModal(false)}
      />

      <SessionSummaryModal
        open={p.showSummaryModal}
        results={p.sessionPlayers.map((sp) => ({
          name: sp.name,
          buyIns: sp.buyIns,
          cashOut: parseFloat(sp.cashOut) || 0,
          net: (parseFloat(sp.cashOut) || 0) - sp.buyIns * p.buyInAmount,
        }))}
        onConfirm={p.endSession}
        saving={p.saving}
        buyInUnit={p.buyInUnit}
        sessionId={p.sessionId}
      />
    </div>
  );
}
